// the clock: at each feature's minute (config/clock.json, Oct 27: Market Close-Up :x3, News Desk :09 and :39 for two
// minutes, Views, the cameras block, from :11 and :41 up to Markets at :13 and :43, and from :26 and :56 up to the :30
// and :00 ID) the loop steps aside for the feature, then picks up again with the screen it was showing. featureRules
// keep Market Close-Up to the markets window (marketsOn). Bumpers (bumper.mjs, lines from cadence.mjs) lead into
// each feature and out of it, and fill the :00 and :30.
// Only while playing: a paused or hand-driven page is left alone.
import {
	currentDisplay, showDisplay, isPlaying, isLocationActive, displayNavMessage, msg,
} from '../navigation.mjs';
import STATUS from '../status.mjs';
import { daypart } from './music.mjs';
import { playBumper, isPlaying as bumperOn } from './bumper.mjs';
import { featureIntro, outro, slot as cadenceSlot } from './cadence.mjs';
import marketState from './market-watch.mjs';
import { peekNext, takeNext, restart } from './rotation.mjs';
import { runShow } from './shows.mjs';

const { clock } = window.RHL;
const features = new Map();
let lastSlot = null;
let resumeTo = null;

const registerFeature = (name, display) => features.set(name, display);

const localTime = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
		timeZone: clock.timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return {
		slot: `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`,
		minute: Number(parts.minute),
		second: Number(parts.second),
	};
};

// something is trading: the NYSE session with pre-market and after hours, index futures, or Seoul and Tokyo
// (09:00-15:30 local, the same hours in both)
const asiaOpen = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: 'Asia/Seoul', hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	const minutes = Number(parts.hour) * 60 + Number(parts.minute);
	return !['Sat', 'Sun'].includes(parts.weekday) && minutes >= 9 * 60 && minutes < 15 * 60 + 30;
};
const marketsMoving = (now) => {
	const state = marketState(now);
	return state.stocks !== 'Markets closed' || state.futuresOpen || asiaOpen(now);
};

// the day of the week (0 = Sunday) and hour in the channel's time zone
const dayHour = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return { day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday), hour: Number(parts.hour) };
};
// Asia has opened for the week: 9:00 Monday in Seoul (Sunday afternoon here)
const asiaWeekOpen = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: 'Asia/Seoul', hour: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return parts.weekday === 'Mon' && Number(parts.hour) >= 9;
};
// Markets (the Close-Up and the board) are on all night on weeknights, Friday night included, and off from Saturday
// morning until Asia opens Sunday (Oct 27, Jared; the cutoff is featureRules.markets.offFrom). On a weekday holiday
// they run only while something trades.
const marketsOn = (now = new Date()) => {
	const { day, hour } = dayHour(now);
	const from = clock.featureRules?.markets?.offFrom ?? { day: 6, hour: 6 };
	if ((day === from.day && hour >= from.hour) || (day === 0 && !asiaWeekOpen(now))) return false;
	const fridayNight = (day === 5 && hour >= 12) || (day === 6 && hour < from.hour);
	return fridayNight || marketsMoving(now);
};

// which feature runs at this minute, and how, after the daypart, weekend and market rules
const featureFor = (minute, now = new Date()) => {
	// a feature runs at `minute` past every ten, or at the minutes of the hour it lists (`minutes`), in the dayparts it
	// names (`dayparts`) and not in the ones it skips (`skipDayparts`; Oct 27, slow nights)
	const { name: part, weekend } = daypart(now);
	const feature = clock.features.find((f) => (f.minutes ? f.minutes.includes(minute) : f.minute === minute % 10)
		&& (!f.dayparts || f.dayparts.includes(part)) && !f.skipDayparts?.includes(part));
	if (!feature) return null;
	const rules = clock.featureRules ?? {};
	let { name } = feature;
	const options = { ...feature.options };
	if (weekend) {
		if (rules.weekend?.drop?.includes(name)) return null;
		name = rules.weekend?.swap?.[name] ?? name;
		if (rules.weekend?.news) options.news = rules.weekend.news;
	}
	if (part === 'night' && rules.night?.only && !rules.night.only.includes(feature.minute)) return null;
	if (rules.marketsOnly?.includes(name) && !marketsOn(now)) return null;
	// a block that runs up to a fixed minute (Views) learns which one
	if (feature.until) options.until = feature.until[feature.minutes.indexOf(minute)];
	// how early it starts: its own `leadSeconds` (Markets on the 3s, on the minute, Oct 29), else the clock's
	return {
		name, options, seconds: feature.seconds, lead: feature.leadSeconds ?? clock.leadSeconds ?? 0,
	};
};

const showable = (display) => display && display.status === STATUS.loaded && display.timing.totalScreens > 0;

// back to the loop: the screen the feature cut away from, from its start, or else the next one; behind the
// feature's outro bumper when it has one
// a feature that ends within this long of :00 or :30 hands straight to the station ID, no outro (Oct 26)
const ID_SOON_MS = 20000;
const HALF_HOUR_MS = 30 * 60000;
const finish = (feature) => {
	const target = showable(resumeTo) ? resumeTo : null;
	resumeTo = null;
	const boundary = Math.ceil(Date.now() / HALF_HOUR_MS) * HALF_HOUR_MS;
	if (boundary - Date.now() <= ID_SOON_MS) {
		const at = localTime(new Date(boundary));
		const spec = cadenceSlot(at.minute, new Date(boundary));
		if (spec) {
			// the ID airs now, so the clock doesn't play it again at the turn of the half hour
			lastSlot = at.slot;
			feature.outroBumper = null;
			// the spine starts again from the top (rotation.mjs)
			restart();
			const next = takeNext();
			playBumper({ ...spec, upNext: spec.upNext ?? next?.name, onCovered: () => (next ? showDisplay(next) : displayNavMessage({ id: feature.navId, type: msg.response.next })) });
			return;
		}
	}
	// a feature due within the same margin (Views at :11 after News, Markets at :13 after Views) follows at once, behind
	// its own intro: no outro and no few seconds of loop between them (Oct 27); the loop screen waits for after it
	const nextAt = Math.ceil((Date.now() + LEAD_MS) / 60000) * 60000;
	const nextLocal = localTime(new Date(nextAt));
	const plan = featureFor(nextLocal.minute, new Date(nextAt));
	const following = plan && features.get(plan.name);
	const go = () => {
		if (target) showDisplay(target);
		else displayNavMessage({ id: feature.navId, type: msg.response.next });
	};
	if (following && following !== feature && nextAt - LEAD_MS - Date.now() <= ID_SOON_MS && following.hasData()) {
		feature.outroBumper = null;
		lastSlot = nextLocal.slot;
		const handOff = () => {
			resumeTo = target;
			if (cutTo(following, plan.options, { intro: featureIntro(plan.name, following.name), outro })) return true;
			lastSlot = null;
			resumeTo = null;
			return false;
		};
		// one that starts on its minute (Markets, Oct 29) holds the last frame until then instead of cutting in early
		const wait = nextAt - plan.lead * 1000 - Date.now();
		if (wait > 0) {
			holding = true;
			setTimeout(() => {
				holding = false;
				if (!handOff()) go();
			}, wait);
			return;
		}
		if (handOff()) return;
	}
	const { outroBumper } = feature;
	feature.outroBumper = null;
	if (outroBumper) playBumper({ ...outroBumper((target ?? peekNext())?.name), onCovered: go });
	else go();
};

// cut away to a feature or an interrupt, behind its intro bumper when it has one. One that interrupts a feature
// drops it (it waits for its next minute) and returns to the loop screen the feature had left; otherwise it
// returns to whatever was showing.
let cutting = false;
// between a feature's end and the next one's minute (finish), nothing else takes the screen
let holding = false;
const cutTo = (display, options = {}, { intro = null, outro: outroBumper = null } = {}) => {
	if (!display.arm({ scheduled: true, options })) return false;
	const current = currentDisplay();
	if (current?.isFeature && current !== display) current.disarm();
	else resumeTo = current ?? null;
	display.onFinish = finish;
	display.outroBumper = outroBumper;
	if (!intro) {
		showDisplay(display);
		return true;
	}
	cutting = true;
	playBumper({ ...intro, onCovered: () => showDisplay(display) }).finally(() => { cutting = false; });
	return true;
};

const busy = () => (currentDisplay()?.isFeature ?? false) || cutting || holding || bumperOn();
const live = () => isPlaying() && isLocationActive();

// a bumper between loop screens, then on to the screen it announced
const runBumper = (spec, { fresh = false } = {}) => {
	if (busy()) return false;
	// after the :00 and :30 ID the loop starts over from its first screen (Oct 26, Jared: "go straight into the cycle
	// fresh")
	if (fresh) restart();
	const next = takeNext();
	playBumper({ ...spec, upNext: spec.upNext ?? next?.name, onCovered: () => next && showDisplay(next) });
	return true;
};

// the :00 and :30 bumper: a station ID, a daypart line or a special
const voiceSlot = (minute, now) => {
	const spec = cadenceSlot(minute, now);
	if (spec) runBumper(spec, { fresh: true });
};

// a feature now, exactly as the clock runs it: intro, pages, outro, back to the loop (the control room uses this)
const runFeature = (name, options = {}) => {
	const feature = features.get(name);
	if (!feature || !feature.hasData() || busy()) return false;
	return cutTo(feature, options, { intro: featureIntro(name, feature.name), outro });
};
const featureNames = () => [...features.keys()];

// features and :00/:30 bumpers start this many seconds before their minute, so the intro plays across the turn of
// the minute and the feature has its whole minute (Oct 26)
const LEAD_MS = (clock.leadSeconds ?? 0) * 1000;
const tick = () => {
	const now = new Date(Date.now() + LEAD_MS);
	const { slot, minute, second } = localTime(now);
	// once per minute, and only near its start, so a page opened at :03:40 doesn't cut away mid-minute
	if (slot === lastSlot || second > 20) return;
	if (!live()) {
		lastSlot = slot;
		return;
	}
	// a feature, an interrupt or a bumper already on air keeps it; try again each second while the minute is young,
	// so a block that follows a feature (Views at :11, after News's two minutes from :09) starts once the outro clears
	if (busy()) return;
	const plan = featureFor(minute, now);
	// a feature with a shorter lead than the clock's (Markets, on the minute) waits for its own start
	const lead = plan ? plan.lead : (clock.leadSeconds ?? 0);
	if (Date.now() < Math.floor(now.getTime() / 60000) * 60000 - lead * 1000) return;
	lastSlot = slot;
	// the show slot (Phase D) is screens in a row, not a feature display
	if (plan?.name === 'show' && runShow(now)) return;
	const feature = plan && features.get(plan.name);
	if (feature && feature.status === STATUS.loaded && feature.hasData()) {
		cutTo(feature, plan.options, { intro: featureIntro(plan.name, feature.name), outro });
		return;
	}
	voiceSlot(minute % 60, now);
};

setInterval(tick, 1000);

export {
	registerFeature,
	featureFor,
	cutTo,
	live,
	busy,
	runFeature,
	runBumper,
	featureNames,
	marketsOn,
};
